import React, { useState, useEffect } from 'react'
import { FiSearch, FiDownload } from 'react-icons/fi'
import { Chip, Pagination, Tooltip } from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import api from '@/services/api'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import ListAltIcon from '@mui/icons-material/ListAlt'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import { toast } from 'react-toastify'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
import Notfound from '../../../Notfound'
import { TableShimmer } from '../../../ShimmerUI'

const featuresOptions = [
  'Scheduling',
  'Secure Messaging',
  'Treatment Plans',
  'Session Notes Access',
  'Progress Tracking',
]

const packageOptions = ['Provider', 'Agency']
const tierOptions = ['Starter', 'Pro']

const PlanManagementPage: React.FC = () => {
  const perPage = 10
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [pageSize, setPageSize] = useState(10)
  const [openToggle, setOpenToggle] = useState(false)
  const [services, setServices] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [plans, setPlans] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const [loadingForShimmer, setLoadingForShimmer] = useState(true)
  const [loading, setLoading] = useState(false)
  const [planName, setPlanName] = useState('')
  const [monthlyPrice, setMonthlyPrice] = useState('')
  const [yearlyPrice, setYearlyPrice] = useState('')
  const [description, setDescription] = useState('')
  const [monthlyCommission, setMonthlyCommission] = useState('')
  const [yearlyCommission, setYearlyCommission] = useState('')
  const [trialDays, setTrialDays] = useState<string | null>(null)
  const [openDelete, setOpenDelete] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [selectedAccountStatus, setSelectedAccountStatus] = useState('')
  const [isActive, setIsActive] = useState<boolean>(false)

  const [validationErrors, setValidationErrors] = useState({
    planName: false,
    description: false,
    monthlyPrice: false,
    yearlyPrice: false,
    monthlyCommission: false,
    yearlyCommission: false,
  })
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalActive, setTotalActive] = useState(0)

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setSelectedPlanId(plan._id)
      setPlanName(plan.name)
      setDescription(plan.description)
      setMonthlyPrice(plan.monthly_fee)
      setYearlyPrice(plan.anual_fee)
      setMonthlyCommission(plan.monthly_commission || '')
      setYearlyCommission(plan.anual_commission || '')
      setTrialDays(
        plan.trialDays !== undefined && plan.trialDays !== null
          ? plan.trialDays.toString()
          : null
      )
    } else {
      // Otherwise, reset fields for add
      setSelectedPlanId(null)
      setPlanName('')
      setDescription('')
      setMonthlyPrice('')
      setYearlyPrice('')
      setMonthlyCommission('')
      setYearlyCommission('')
      setTrialDays(null)
    }
    setOpenDialog(true)
  }

  const handleOpenToggle = (planId: string, currentStatus: boolean) => {
    setSelectedPlanId(planId) // Set the selected plan to toggle
    setIsActive(currentStatus) // Set the current status of the plan (active or inactive)
    setOpenToggle(true) // Open the confirmation dialog
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setValidationErrors({
      planName: false,
      description: false,
      monthlyPrice: false,
      yearlyPrice: false,
      monthlyCommission: false,
      yearlyCommission: false,
    })
  }
  const handleAccountStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccountStatus(event.target.value)
  }

  const validateForm = () => {
    const errors = {
      planName: !planName.trim(),
      description: !description.trim(),
      monthlyPrice: !monthlyPrice.toString().trim(),
      yearlyPrice: !yearlyPrice.toString().trim(),
      monthlyCommission: !monthlyCommission.toString().trim(),
      yearlyCommission: !yearlyCommission.toString().trim(),
    }

    setValidationErrors(errors)
    return Object.values(errors).every(error => !error)
  }

  // Open confirmation dialog
  const handleOpenDelete = (planId: string) => {
    setSelectedPlanId(planId)
    setOpenDelete(true)
  }

  // Close confirmation dialog
  const handleCloseDelete = () => {
    setOpenDelete(false)
    setSelectedPlanId(null)
  }
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
    fetchPlans(searchKeyword, page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }
  const fetchPlans = async (
    searchKeyword?: string,
    page?: number,
    startDate?: Date | null,
    endDate?: Date | null
  ) => {
    setLoading(true)
    setError('')
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        page: page || 1, // Default to 1 if page is not provided
        limit: 10,
        accountStatus: selectedAccountStatus,
      }

      // If searchKeyword exists, add it to params
      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }
      const response: any = await api.get('membership/get-plans', { params })

      console.log('API response:', response?.data)

      // Set plans from response data
      setPlans(response?.data?.membership || [])
      setTotalRecords(response?.data?.totalRecords || 0)
      setTotalActive(response?.data?.totalActive || 0)
      setTotalPages(Math.ceil(response?.data?.totalRecords / pageSize))
    } catch (err: any) {
      console.error('Failed to fetch plans:', err)
      setError(err.response?.data?.message || 'Failed to fetch plans')
    } finally {
      setLoadingForShimmer(false)
      setLoading(false)
    }
  }

  const handleAddOrUpdatePlan = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const payload = {
        name: planName,
        description,
        monthly_fee: monthlyPrice,
        anual_fee: yearlyPrice,
        monthly_commission: monthlyCommission,
        anual_commission: yearlyCommission,
        trialDays: trialDays ? Number(trialDays) : null,
      }

      if (selectedPlanId) {
        await api.put(`/membership/update-plan/${selectedPlanId}`, payload)
        toast.success('Plan updated successfully!')
      } else {
        await api.post('/membership/create-plan', payload)
        toast.success('Plan created successfully!')
      }

      handleCloseDialog()
      fetchPlans()

      // Reset fields
      setPlanName('')
      setDescription('')
      setMonthlyPrice('')
      setYearlyPrice('')
      setMonthlyCommission('')
      setYearlyCommission('')
      setTrialDays(null)
    } catch (error) {
      console.error('Error creating/updating plan:', error)
      toast.error('Failed to save plan')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedPlanId) return
    setLoading(true)
    try {
      const response = await api.delete(
        `/membership/delete-plan/${selectedPlanId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )

      toast.success('Plan deleted successfully!')
      handleCloseDelete()
      fetchPlans() // Refresh the plan list
    } catch (error) {
      console.error('Error deleting plan:', error)
      toast.error('Failed to delete plan.')
    } finally {
      setLoading(false)
    }
  }
  // const handleOpenToggle = async (planId: string) => {
  //   try {
  //     const newStatus = isActive ? 'inactive' : 'active'

  //     // Call the API to update the plan status
  //     const response = await api.patch(`membership/plan-status/${planId}`, {
  //       status: newStatus,
  //     })

  //     if (response.status === 200) {
  //       // On success, toggle the status locally
  //       setIsActive(!isActive)
  //       toast.success(`Plan status updated to ${newStatus}`)
  //     }
  //     fetchPlans()
  //   } catch (error) {
  //     console.error('Error updating the plan status:', error)
  //     toast.error('Failed to update plan status')
  //   }
  // }
  const handleConfirmToggle = async () => {
    if (!selectedPlanId) return // Exit if no plan is selected
    setLoading(true) // Start loading

    const newStatus = isActive ? 'inactive' : 'active' // Toggle the status
    try {
      const response = await api.patch(
        `membership/plan-status/${selectedPlanId}`,
        {
          status: newStatus,
        }
      )

      if (response.status === 200) {
        setIsActive(!isActive) // Toggle status locally
        toast.success(`Plan status updated to ${newStatus}`)
      }

      setOpenToggle(false) // Close dialog after success
      fetchPlans() // Refresh the plan list after updating status
    } catch (error) {
      console.error('Error updating the plan status:', error)
      toast.error('Failed to update plan status')
    } finally {
      setLoading(false) // Stop loading
    }
  }
  const handleReset = () => {
    setSearchKeyword('')
    setSelectedAccountStatus('')
  }
  useEffect(() => {
    fetchPlans(searchKeyword)
  }, [searchKeyword, selectedAccountStatus])

  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header + Search + Export */}
      <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
        <h1 className="text-2xl font-semibold text-[#6E7B8B]">
          Plan Management
        </h1>
        {/*  <button className="flex items-center gap-2 px-4 py-2 text-white bg-[#5BC0BE] border border-gray-300 rounded-lg hover:bg-[#49a9a7]">
          <FiDownload className="w-4 h-4" />
          Export CSV
        </button> */}
      </div>

      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          boxShadow: 2,
          padding: 2,
          mb: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <div className="grid items-end grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Search Plan
              </label>
              <input
                type="text"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="Search Plan"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
              />
            </div>

            <div className="flex items-center space-x-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Account Status
                </label>
                <select
                  id="accountStatus"
                  value={selectedAccountStatus}
                  onChange={handleAccountStatusChange}
                  className="w-[152px]  border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
                >
                  <option value="" disabled selected>
                    Select Status
                  </option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Reset Button next to Account Status dropdown */}
              <Box display="flex" alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    borderRadius: 2,
                    width: '113px',
                    marginTop: '24px',
                  }}
                >
                  Reset All
                </Button>
              </Box>
            </div>

            <div className="hidden col-span-2 md:block"></div>

            <div className="flex items-end justify-end mt-6">
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleOpenDialog()}
              >
                + Add Plan
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow h-35">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{totalRecords}</h2>
            <p className="text-sm text-gray-500">Total Plan</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-green-600 bg-green-100 rounded-full">
            <ListAltIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{totalActive}</h2>
            <p className="text-sm text-gray-500">Active Plan</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-blue-600 bg-blue-100 rounded-full">
            <CheckIcon />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {totalRecords - totalActive}
            </h2>
            <p className="text-sm text-gray-500">InActive Plan</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 text-xl font-bold text-red-600 bg-red-100 rounded-full">
            <ClearIcon />
          </div>
        </div>
      </div>

      {/* Plan Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-white uppercase bg-[#5BC0BE]">
                <th className="px-6 py-3 font-medium">Plan Name</th>
                <th className="px-6 py-3 font-medium">Description</th>
                <th className="px-6 py-3 font-medium">Price</th>
                <th className="px-6 py-3 font-medium">Commission</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loadingForShimmer ? (
                <TableShimmer columns={7} />
              ) : plans?.length === 0 ? (
                <tr>
                  <td colSpan={20} className="p-4 text-center">
                    <Notfound />
                  </td>
                </tr>
              ) : (
                plans.map(plan => (
                  <tr
                    key={plan._id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">{plan.name}</td>
                    <td className="px-6 py-4">{plan.description}</td>

                    {/* <td className="px-6 py-4">{plan.planType}</td> */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <Chip
                          label={`Monthly: $${plan.monthly_fee}.00`}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={`Yearly: $${plan.anual_fee}.00`}
                          color="secondary"
                          size="small"
                        />
                        {plan.trialDays > 0 && (
                          <Chip
                            label={`Trial: ${plan.trialDays} day${
                              plan.trialDays > 1 ? 's' : ''
                            }`}
                            color="success"
                            size="small"
                          />
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <Chip
                          label={`Monthly: ${plan.monthly_commission || '0.00'}%`}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={`Yearly: ${plan.anual_commission || '0.00'}%`}
                          color="secondary"
                          size="small"
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          plan.isActive
                            ? 'border-green-500 text-green-600'
                            : 'border-red-500 text-red-600'
                        }`}
                      >
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    <td className="flex gap-2 px-6 py-4 mt-6">
                      <Tooltip title="Edit Plan" arrow>
                        <IconButton onClick={() => handleOpenDialog(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {/* Tooltip for Delete */}
                      <Tooltip title="Delete Plan" arrow>
                        <IconButton onClick={() => handleOpenDelete(plan._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>

                      {/* Tooltip for Toggle Status */}
                      <Tooltip
                        title={
                          plan.isActive ? 'Deactivate Plan' : 'Activate Plan'
                        }
                        arrow
                      >
                        <IconButton
                          onClick={() =>
                            handleOpenToggle(plan._id, plan.isActive)
                          }
                        >
                          <HelpIcon />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
          <RowsPerPageDropdown
            pageSize={pageSize}
            onPageSizeChange={newPageSize => {
              handlePageSizeChange(newPageSize)
              if (ourPageSize && ourPageSize !== newPageSize) {
                setCurrentPage(1)
              }
            }}
          />
        </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-1 mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>

      {/* Add Plan Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedPlanId ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
        <DialogContent className="flex flex-col gap-4">
          <TextField
            label="Plan Name"
            value={planName}
            onChange={e => setPlanName(e.target.value)}
            error={validationErrors.planName}
            helperText={
              validationErrors.planName ? 'Plan Name is required' : ''
            }
            fullWidth
            sx={{ mt: '5px' }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            error={validationErrors.description}
            helperText={
              validationErrors.description ? 'Description is required' : ''
            }
          />

          <TextField
            label="Monthly Price"
            type="number"
            value={monthlyPrice}
            onChange={e => {
              const inputValue = e.target.value

              // Prevent values less than or equal to 0
              if (parseFloat(inputValue) <= 0 || inputValue === '') {
                setMonthlyPrice('') // Clear the input field if invalid
                return
              }

              // Allow only positive decimal numbers (up to 2 decimal places)
              const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                ? inputValue
                : monthlyPrice // Keep previous value if invalid

              setMonthlyPrice(validValue)
            }}
            fullWidth
            error={validationErrors.monthlyPrice}
            helperText={
              validationErrors.monthlyPrice ? 'Monthly Price is required' : ''
            }
          />

          <TextField
            label="Monthly Commission"
            type="number"
            value={monthlyCommission}
            onChange={e => {
              const inputValue = e.target.value

              // Prevent values less than or equal to 0
              if (parseFloat(inputValue) <= 0 || inputValue === '') {
                setMonthlyCommission('') // Clear the input field if invalid
                return
              }

              // Allow only positive decimal numbers (up to 2 decimal places)
              const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                ? inputValue
                : monthlyCommission // Keep previous value if invalid

              setMonthlyCommission(validValue)
            }}
            fullWidth
            error={validationErrors.monthlyCommission}
            helperText={
              validationErrors.monthlyCommission
                ? 'Monthly Commission is required'
                : ''
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />

          <TextField
            label="Yearly Price"
            type="number"
            value={yearlyPrice}
            onChange={e => {
              const inputValue = e.target.value

              // Prevent values less than or equal to 0
              if (parseFloat(inputValue) <= 0 || inputValue === '') {
                setYearlyPrice('') // Clear the input field if invalid
                return
              }

              // Allow only positive decimal numbers (up to 2 decimal places)
              const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                ? inputValue
                : yearlyPrice // Keep previous value if invalid

              setYearlyPrice(validValue)
            }}
            fullWidth
            error={validationErrors.yearlyPrice}
            helperText={
              validationErrors.yearlyPrice ? 'Yearly Price is required' : ''
            }
          />

          <TextField
            label="Yearly Commission"
            type="number"
            value={yearlyCommission}
            onChange={e => {
              const inputValue = e.target.value

              // Prevent values less than or equal to 0
              if (parseFloat(inputValue) <= 0 || inputValue === '') {
                setYearlyCommission('') // Clear the input field if invalid
                return
              }

              // Allow only positive decimal numbers (up to 2 decimal places)
              const validValue = inputValue.match(/^(\d+(\.\d{0,2})?)?$/)
                ? inputValue
                : yearlyCommission // Keep previous value if invalid

              setYearlyCommission(validValue)
            }}
            fullWidth
            error={validationErrors.yearlyCommission}
            helperText={
              validationErrors.yearlyCommission
                ? 'Yearly Commission is required'
                : ''
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />

          <TextField
            label="Trial Days"
            type="number"
            value={trialDays ?? ''}
            onChange={e => {
              const value = e.target.value

              if (value === '') {
                setTrialDays(null)
                return
              }

              if (/^\d+$/.test(value)) {
                setTrialDays(value)
              }
            }}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddOrUpdatePlan}
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : selectedPlanId
                ? 'Update Plan'
                : 'Add Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={handleCloseDelete}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this plan?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openToggle} onClose={() => setOpenToggle(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to {isActive ? 'deactivate' : 'activate'} this
          plan?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenToggle(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </div>
  )
}

export default PlanManagementPage
